import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';
import { uint8ArrayToBase64 } from '../utils/yjs';

export interface DiagramVersionSummary {
  version: number;
  label: string | null;
  note: string | null;
  type: 'system' | 'user';
  createdAt: string;
  createdBy: string | null;
}

export interface DiagramVersionDetails extends DiagramVersionSummary {
  snapshotBytes: string | null;
  stateVector: string;
  downloadUrl?: string;
}

interface CreateVersionInput {
  label?: string;
  note?: string;
}

interface RenameInput {
  version: number;
  label: string | null;
}

export function useDiagramVersions(diagramId?: string) {
  const fetcher = useAuthenticatedFetch();
  const qc = useQueryClient();
  const enabled = Boolean(diagramId);

  const versions = useQuery({
    queryKey: ['diagram', diagramId, 'versions'],
    enabled,
    queryFn: async (): Promise<DiagramVersionSummary[]> => {
      const res = await fetcher(`/api/diagrams/${diagramId}/versions`);
      if (!res.ok) throw new Error('Failed to fetch versions');
      const json = await res.json();
      const items = Array.isArray(json.versions) ? json.versions : [];
      return items.map((item: any) => ({
        version: Number(item.version),
        label: item.label ?? null,
        note: item.note ?? null,
        type: item.type ?? 'system',
        createdAt: item.createdAt || '',
        createdBy: item.createdBy ?? null,
      }));
    },
  });

  const createVersion = useMutation({
    mutationFn: async (input: CreateVersionInput = {}) => {
      const res = await fetcher(`/api/diagrams/${diagramId}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: input.label, note: input.note }),
      });
      if (!res.ok) throw new Error('Failed to create version');
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'versions'] });
    },
  });

  const restoreVersion = useMutation({
    mutationFn: async (version: number) => {
      const res = await fetcher(`/api/diagrams/${diagramId}/versions/${version}/restore`, {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error('Failed to restore version');
      }
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'versions'] });
    },
  });

  const renameVersion = useMutation({
    mutationFn: async ({ version, label }: RenameInput) => {
      const res = await fetcher(`/api/diagrams/${diagramId}/versions/${version}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) throw new Error('Failed to rename version');
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'versions'] });
    },
  });

  const getVersionDetails = async (version: number): Promise<DiagramVersionDetails> => {
    if (!diagramId) {
      throw new Error('Missing diagram id');
    }
    const res = await fetcher(`/api/diagrams/${diagramId}/versions/${version}`);
    if (!res.ok) {
      throw new Error('Failed to load version');
    }
    const json = await res.json();
    const payload = json?.version;
    if (!payload) {
      throw new Error('Invalid version payload');
    }

    let snapshotBytes: string | null = payload.snapshotBytesBase64 || null;

    // R2 path: download binary and convert to base64 so consumers always get snapshotBytes
    if (!snapshotBytes && payload.downloadUrl) {
      const r2Response = await fetch(payload.downloadUrl);
      if (!r2Response.ok) {
        throw new Error(`Failed to download version from R2: ${r2Response.status}`);
      }
      const buffer = await r2Response.arrayBuffer();
      snapshotBytes = uint8ArrayToBase64(new Uint8Array(buffer));
    }

    return {
      version: Number(payload.version),
      label: payload.label ?? null,
      note: payload.note ?? null,
      type: payload.type ?? 'system',
      createdAt: payload.createdAt || '',
      createdBy: payload.createdBy ?? null,
      snapshotBytes,
      stateVector: payload.stateVectorBase64 || '',
      downloadUrl: payload.downloadUrl,
    };
  };

  return {
    versions,
    createVersion,
    restoreVersion,
    renameVersion,
    getVersionDetails,
  };
}
